import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface ProductOption {
  id: number
  sku: string | null
  name: string
  price: number
  category: string | null
  sessionCount: number | null
  active: boolean
}

type ProductsResponse = { products: ProductOption[] }

interface Props {
  value: string
  onChange: (name: string, price?: number) => void
  placeholder?: string
  className?: string
}

// Searchable catalog combobox. Keeps whatever the user types as the item
// name — so staff can still enter a one-off line with a name that doesn't
// exist in the catalog. The caller decides whether to persist an unknown
// name as a new product on submit.
export default function ProductCombobox({ value, onChange, placeholder, className }: Props) {
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [anchor, setAnchor] = useState<{ left: number; top: number; width: number } | null>(null)

  const { data } = useQuery<ProductsResponse>({
    queryKey: ['products', 'active'],
    queryFn: () => api<ProductsResponse>('/products?active=true'),
    staleTime: 5 * 60 * 1000,
  })

  const products = data?.products ?? []

  const query = (value ?? '').trim().toLowerCase()
  const matches = useMemo(() => {
    if (!query) return products.slice(0, 20)
    return products
      .filter((p) => p.name.toLowerCase().includes(query))
      .slice(0, 20)
  }, [products, query])

  // Clamp highlight whenever filtered list shrinks so the selection ring
  // never lands on an out-of-range row.
  useEffect(() => {
    if (highlight >= matches.length) setHighlight(Math.max(0, matches.length - 1))
  }, [matches.length, highlight])

  // Close dropdown on outside click.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const el = e.target as Node | null
      if (rootRef.current?.contains(el)) return
      // Clicks inside the portal-rendered dropdown carry a data attribute on
      // their ancestor <ul> so we can tell them apart from true outside clicks.
      if (el instanceof Element && el.closest('[data-combobox-dropdown]')) return
      setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  // Keep the portaled dropdown glued to the input — recompute on open, on
  // window resize, and on any scroll in the capture phase (so nested scroll
  // containers — e.g. the POS items table — reposition it instead of letting
  // it float off the input).
  useEffect(() => {
    if (!open) return
    function reposition() {
      const el = inputRef.current
      if (!el) return
      const r = el.getBoundingClientRect()
      setAnchor({ left: r.left, top: r.bottom, width: r.width })
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open])

  function pick(p: ProductOption) {
    onChange(p.name, p.price)
    setOpen(false)
  }

  return (
    <div ref={rootRef} className={`relative ${className ?? ''}`}>
      <input
        ref={inputRef}
        className="input"
        placeholder={placeholder ?? 'ชื่อรายการ'}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
          setHighlight(0)
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open) return
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlight((h) => Math.min(h + 1, matches.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlight((h) => Math.max(h - 1, 0))
          } else if (e.key === 'Enter') {
            if (matches[highlight]) {
              e.preventDefault()
              pick(matches[highlight])
            }
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {open &&
        anchor &&
        // Rendered via portal so the narrow scroll-clipping ancestor in the
        // POS items table can't cut the dropdown off. position:fixed anchored
        // to the input's viewport rect; reposition effect keeps it glued on
        // scroll/resize. min-w keeps it readable when the input column is
        // narrow, max-w stops it from overflowing a skinny viewport.
        createPortal(
          matches.length > 0 ? (
            <ul
              data-combobox-dropdown
              style={{
                position: 'fixed',
                left: anchor.left,
                top: anchor.top + 4,
                minWidth: Math.max(320, anchor.width),
                maxWidth: Math.min(576, window.innerWidth - 32),
              }}
              className="z-50 max-h-80 overflow-auto rounded-md border border-slate-200 bg-white shadow-xl text-sm"
            >
              {matches.map((p, i) => (
                <li
                  key={p.id}
                  className={`px-3 py-1.5 cursor-pointer flex items-center justify-between gap-3 whitespace-nowrap ${
                    i === highlight ? 'bg-brand-50 text-brand-900' : 'hover:bg-slate-50'
                  }`}
                  onMouseEnter={() => setHighlight(i)}
                  // mousedown fires before input blur — keeps the click from
                  // dismissing the dropdown before pick() runs.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    pick(p)
                  }}
                >
                  <span>{p.name}</span>
                  <span className="text-xs text-slate-500 shrink-0">
                    {p.category ? `${p.category} · ` : ''}
                    {p.price.toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          ) : query ? (
            <div
              data-combobox-dropdown
              style={{
                position: 'fixed',
                left: anchor.left,
                top: anchor.top + 4,
                minWidth: Math.max(320, anchor.width),
                maxWidth: Math.min(576, window.innerWidth - 32),
              }}
              className="z-50 rounded-md border border-slate-200 bg-white shadow-xl px-3 py-2 text-xs text-slate-500"
            >
              ไม่พบในแคตตาล็อก — รายการนี้จะถูกเพิ่มใหม่เมื่อบันทึกใบเสร็จ
            </div>
          ) : null,
          document.body,
        )}
    </div>
  )
}
